/*
 * Zarafae "Cut to Measure": expected delivery date in the order timeline
 * (blocks/zarafae-order-timeline.liquid, critique item under-atc-timeline-whatsapp).
 *
 * Only a timeline with "Show expected date" on prints [data-zf-timeline]. The setting is off by
 * default until the client confirms a dated promise (owner adjustment). When it is on, the last
 * step's title becomes "By Mon 28 Sep": today plus the block's day count, in the shopper's own
 * calendar. Without JavaScript, or if the date can't be formatted, the step keeps the Liquid
 * fallback ("Within 2 weeks").
 *
 * Format (feasibility adjustment): Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric',
 * month: 'short' }).formatToParts(), with the weekday and month cut to three letters. Current ICU
 * writes the en-GB short month for September as "Sept", so the parts are assembled here rather
 * than taken from format().
 *
 * Re-applied on pageshow, on shopify:section:load (theme editor) and once a variant change settles
 * (shopify:product:select), because the product form morphs the section back to the server text.
 * One-time document-level listeners behind a window flag; loaded with type="module".
 */

const TIMELINE = '[data-zf-timeline]';
const DATE_SLOT = '[data-zf-timeline-date]';
const DATE_TOKEN = '[date]';

/**
 * @param {Intl.DateTimeFormatPart[]} parts
 * @param {string} type
 */
function partValue(parts, type) {
  const found = parts.find((part) => part.type === type);
  return found ? found.value.replace(/\.$/, '') : '';
}

/**
 * "Mon 28 Sep".
 * @param {Date} date
 * @returns {string} An empty string when Intl can't format the date.
 */
function formatDeliveryDate(date) {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    }).formatToParts(date);
    const weekday = partValue(parts, 'weekday').slice(0, 3);
    const day = partValue(parts, 'day');
    const month = partValue(parts, 'month').slice(0, 3);
    return weekday && day && month ? `${weekday} ${day} ${month}` : '';
  } catch (error) {
    return '';
  }
}

/** @param {Date} date */
function isoDate(date) {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** @param {Element} timeline */
function applyDate(timeline) {
  const slot = timeline.querySelector(DATE_SLOT);
  if (!slot) return;

  const days = Number.parseInt(timeline.getAttribute('data-zf-delivery-days') || '', 10);
  if (!Number.isFinite(days) || days < 1) return;

  // Midday keeps a daylight-saving change from moving the date by one.
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  date.setDate(date.getDate() + days);

  const label = formatDeliveryDate(date);
  if (!label) return;

  const template = (timeline.getAttribute('data-zf-date-title') || '').trim();
  let text = label;
  if (template.includes(DATE_TOKEN)) {
    text = template.replace(DATE_TOKEN, label);
  } else if (template) {
    text = `${template} ${label}`;
  }

  let time = slot.querySelector('time');
  if (!time) {
    time = document.createElement('time');
    slot.replaceChildren(time);
  }
  time.dateTime = isoDate(date);
  time.textContent = text;
}

function applyAll() {
  document.querySelectorAll(TIMELINE).forEach(applyDate);
}

function init() {
  window.addEventListener('pageshow', applyAll);
  document.addEventListener('shopify:section:load', applyAll);

  document.addEventListener(
    'shopify:product:select',
    (event) => {
      const promise = /** @type {{ promise?: Promise<unknown> }} */ (/** @type {unknown} */ (event)).promise;
      if (!promise || typeof promise.then !== 'function') return;
      const settle = () => setTimeout(applyAll, 0);
      promise.then(settle, settle);
    },
    true
  );

  applyAll();
}

if (!window.__zfProductTimelineInit) {
  window.__zfProductTimelineInit = true;
  init();
}
